import React, { useCallback, useEffect, useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Spin, Tooltip, message } from "antd";
import {
  createSprTovParam,
  createSprTovParamValue,
  deleteSprTovParam,
  deleteSprTovParamValue,
  fetchSprTovParams,
  updateSprTovParam,
  updateSprTovParamValue
} from "./spr-tov-api";

const errMessage = (e, fallback) => e?.message || fallback;

const InlineEdit = ({ initial, onSave, onCancel }) => {
  const [text, setText] = useState(initial);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus({ cursor: "all" });
  }, []);

  return (
    <Input
      ref={inputRef}
      className="spr-tov-params__edit-input"
      size="small"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onPressEnter={() => onSave(text)}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
      onBlur={onCancel}
    />
  );
};

const RowActions = ({ onEdit, onDelete }) => (
  <span className="spr-tov-params__row-actions" onClick={(e) => e.stopPropagation()}>
    <Tooltip title="Изменить">
      <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} />
    </Tooltip>
    <Tooltip title="Удалить">
      <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={onDelete} />
    </Tooltip>
  </span>
);

/**
 * Вкладка «Параметры» карточки услуги: параметры и их значения (SPR_TOV_PARAM_*).
 */
const SprTovParams = ({ idTov }) => {
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParamId, setSelectedParamId] = useState(null);
  const [editing, setEditing] = useState(null); // { scope: "param"|"value", mode: "create"|id, extra }
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    fetchSprTovParams(idTov)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const list = Array.isArray(data) ? data : [];
        setParams(list);
        setSelectedParamId((prev) =>
          list.some((p) => p.idParamName === prev) ? prev : list[0]?.idParamName ?? null
        );
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        message.error(errMessage(e, "Не удалось загрузить параметры"));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [idTov]);

  useEffect(() => {
    setEditing(null);
    setSelectedParamId(null);
    load();
  }, [load]);

  const selectedParam = params.find((p) => p.idParamName === selectedParamId) || null;

  const saveParam = useCallback(
    (text, paramId) => {
      const call =
        paramId == null
          ? createSprTovParam(idTov, { name: text })
          : updateSprTovParam(idTov, paramId, { name: text });
      call
        .then(() => {
          setEditing(null);
          load();
        })
        .catch((e) => message.error(errMessage(e, "Не удалось сохранить параметр")));
    },
    [idTov, load]
  );

  const saveValue = useCallback(
    (text, valueId) => {
      const call =
        valueId == null
          ? createSprTovParamValue(idTov, selectedParamId, { fvalue: text })
          : updateSprTovParamValue(idTov, selectedParamId, valueId, { fvalue: text });
      call
        .then(() => {
          setEditing(null);
          load();
        })
        .catch((e) => message.error(errMessage(e, "Не удалось сохранить значение")));
    },
    [idTov, selectedParamId, load]
  );

  const removeParam = useCallback(
    (param) => {
      Modal.confirm({
        title: "Удалить параметр?",
        content: param.name,
        okText: "Удалить",
        cancelText: "Отмена",
        okButtonProps: { danger: true },
        onOk: () =>
          deleteSprTovParam(idTov, param.idParamName)
            .then(load)
            .catch((e) => {
              message.error(errMessage(e, "Не удалось удалить параметр"));
              return Promise.reject(e);
            })
      });
    },
    [idTov, load]
  );

  const removeValue = useCallback(
    (value) => {
      Modal.confirm({
        title: "Удалить значение параметра?",
        content: value.fvalue,
        okText: "Удалить",
        cancelText: "Отмена",
        okButtonProps: { danger: true },
        onOk: () =>
          deleteSprTovParamValue(idTov, selectedParamId, value.idParamValue)
            .then(load)
            .catch((e) => {
              message.error(errMessage(e, "Не удалось удалить значение"));
              return Promise.reject(e);
            })
      });
    },
    [idTov, selectedParamId, load]
  );

  const isEditing = (scope, id) =>
    editing?.scope === scope && editing?.mode === id;

  return (
    <div className="spr-tov-card__tab">
      <div className="spr-tov-card__body spr-tov-params">
        <div className="spr-tov-params__col">
          <div className="spr-tov-params__head">
            <span className="spr-tov-params__title">Параметры</span>
            <Tooltip title="Добавить параметр">
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                aria-label="Добавить параметр"
                onClick={() => setEditing({ scope: "param", mode: "create" })}
              />
            </Tooltip>
          </div>
          <div className="spr-tov-params__list">
            {loading && !params.length ? (
              <div className="spr-tov-params__center">
                <Spin size="small" />
              </div>
            ) : (
              <>
                {params.map((p) =>
                  isEditing("param", p.idParamName) ? (
                    <div key={p.idParamName} className="spr-tov-params__row spr-tov-params__row--editing">
                      <InlineEdit
                        initial={p.name}
                        onSave={(text) => saveParam(text, p.idParamName)}
                        onCancel={() => setEditing(null)}
                      />
                    </div>
                  ) : (
                    <div
                      key={p.idParamName}
                      className={`spr-tov-params__row${p.idParamName === selectedParamId ? " spr-tov-params__row--selected" : ""}`}
                      onClick={() => setSelectedParamId(p.idParamName)}
                    >
                      <span className="spr-tov-params__row-text" title={p.name}>
                        {p.name}
                      </span>
                      <RowActions
                        onEdit={() => setEditing({ scope: "param", mode: p.idParamName })}
                        onDelete={() => removeParam(p)}
                      />
                    </div>
                  )
                )}
                {isEditing("param", "create") ? (
                  <div className="spr-tov-params__row spr-tov-params__row--editing">
                    <InlineEdit
                      initial="Новый параметр"
                      onSave={(text) => saveParam(text, null)}
                      onCancel={() => setEditing(null)}
                    />
                  </div>
                ) : null}
                {!params.length && !isEditing("param", "create") ? (
                  <div className="spr-tov-params__empty">Нет параметров</div>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className="spr-tov-params__col">
          <div className="spr-tov-params__head">
            <span className="spr-tov-params__title" title={selectedParam?.name}>
              Значения{selectedParam ? `: ${selectedParam.name}` : ""}
            </span>
            <Tooltip title="Добавить значение">
              <span>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  aria-label="Добавить значение"
                  disabled={!selectedParam}
                  onClick={() => setEditing({ scope: "value", mode: "create" })}
                />
              </span>
            </Tooltip>
          </div>
          <div className="spr-tov-params__list">
            {!selectedParam ? (
              <div className="spr-tov-params__empty">Выберите параметр</div>
            ) : (
              <>
                {(selectedParam.values || []).map((v) =>
                  isEditing("value", v.idParamValue) ? (
                    <div key={v.idParamValue} className="spr-tov-params__row spr-tov-params__row--editing">
                      <InlineEdit
                        initial={v.fvalue}
                        onSave={(text) => saveValue(text, v.idParamValue)}
                        onCancel={() => setEditing(null)}
                      />
                    </div>
                  ) : (
                    <div key={v.idParamValue} className="spr-tov-params__row">
                      <span className="spr-tov-params__row-text" title={v.fvalue}>
                        {v.fvalue}
                      </span>
                      <RowActions
                        onEdit={() => setEditing({ scope: "value", mode: v.idParamValue })}
                        onDelete={() => removeValue(v)}
                      />
                    </div>
                  )
                )}
                {isEditing("value", "create") ? (
                  <div className="spr-tov-params__row spr-tov-params__row--editing">
                    <InlineEdit
                      initial="Новое значение"
                      onSave={(text) => saveValue(text, null)}
                      onCancel={() => setEditing(null)}
                    />
                  </div>
                ) : null}
                {!(selectedParam.values || []).length && !isEditing("value", "create") ? (
                  <div className="spr-tov-params__empty">Нет значений</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SprTovParams;
